import { defineStore } from 'pinia';
import mapboxgl, { MapEventType } from 'mapbox-gl';
import {
  MapInstance,
  BaseLayersType,
  BaseLayerEnums,
  BaseLayerConfig,
  HoverFarmType,
  MapTooltip,
  ZoomLevels,
} from '../types';
import { useApiStore } from 'src/core/stores/api';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';

type MapStore = {
  mapInstance: MapInstance;
  hoverFarms: HoverFarmType[];
  hoverFarmTooltip: MapTooltip;
  selectedBaseLayer: BaseLayersType;
  zoomLevels: ZoomLevels;
  currentZoomLevel: number;
  mapMaxZoom: number;
  mapMinZoom: number;
};

// Event listener tracking using Map for O(1) lookups
const eventListenerMap = new Map<string, (...args: any[]) => void>();
export const defaultBaseLayer = BaseLayerEnums.MAP_SATELLITE;

// Cache for fetched Mapbox styles to avoid repeated network requests
const styleCache = new Map<string, any>();

export const useMapStore = defineStore('mapStore', {
  state: () =>
    ({
      mapInstance: {
        map: null,
        baseLayerRegistry: new Map<string, BaseLayerConfig>([
          [
            BaseLayerEnums.MAP_SATELLITE,
            {
              id: BaseLayerEnums.MAP_SATELLITE,
              name: 'Satellite',
              type: 'raster',
              url: 'mapbox://mapbox.satellite',
              initialized: false,
              visible: false,
              terrain: true,
              sky: true,
            },
          ]
          // future base layers will be added here
        ]),
        baseLayerSourceIds: new Map(),
        baseLayerLayerIds: new Map(),
        addedListeners: [],
        loadedTiles: [],
      },
      hoverFarms: [],
      hoverFarmTooltip: {
        show: false,
        farms: [],
        x: 0,
        y: 0,
      },
      selectedBaseLayer: defaultBaseLayer,
      zoomLevels: {
        mapview_zoom_threshold: 13,
        commodity_zoom_threshold: [],
      },
      currentZoomLevel: 13,
      mapMaxZoom: 18,
      mapMinZoom: 0,
    } as MapStore),
  actions: {
    /**
     * Initializes the map with the specified container, base layer, and customizations.
     * @param container - The HTML element that will contain the map.
     * @param baseLayer - The type of base layer to use for the map.
     * @param customizations - Optional customizations for the map, such as center, bounds, zoom, bearing, and pitch.
     * @param mapLoadHook - Optional callback function to be called when the map is loaded.
     * @returns {Promise<void>} - A promise that resolves when the map is initialized.
     */
    async initializeMap(
      container: HTMLElement,
      baseLayer: BaseLayersType,
      customizations?: {
        center?: [number, number];
        bounds?: [[number, number], [number, number]];
        zoom?: number;
        bearing?: number;
        pitch?: number;
      },
      mapLoadHook?: (baseLayer: BaseLayersType) => void
    ) {
      if (!this.mapInstance.map) {
        // Create map with a blank style initially (with glyphs for text rendering)
        // @ts-ignore: Type instantiation is excessively deep and possibly infinite.
        this.mapInstance.map = new mapboxgl.Map({
          container: container,
          style: {
            version: 8,
            sources: {},
            layers: [],
            glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
            sprite: 'mapbox://sprites/mapbox/outdoors-v12',
          },
          center: customizations?.center ?? undefined,
          bounds: customizations?.bounds ?? undefined,
          zoom: customizations?.zoom ?? 9,
          bearing: customizations?.bearing ?? 0,
          pitch: customizations?.pitch ?? 0,
          maxZoom: this.mapMaxZoom,
          minZoom: this.mapMinZoom,
          boxZoom: false,
          preserveDrawingBuffer: true,
        });

        this.selectedBaseLayer = baseLayer;

        // Wait for map to load, then initialize base layer
        this.mapInstance.map.once('load', async () => {
          // Initialize and show the default base layer
          await this.initializeBaseLayer(baseLayer);
          await this.setBaseLayerVisibility(baseLayer, true);

          // Call mapLoadHook if provided
          if (mapLoadHook) {
            mapLoadHook(baseLayer);
          }
        });
      } else if (this.selectedBaseLayer !== baseLayer) {
        // Switch to different base layer
        await this.switchBaseLayer(baseLayer);
        if (mapLoadHook) {
          mapLoadHook(baseLayer);
        }
      }
    },

    /**
     * Initialize a base layer (lazy loading)
     */
    async initializeBaseLayer(layerId: string) {
      const config = this.mapInstance.baseLayerRegistry.get(layerId);
      if (!config || config.initialized || !this.mapInstance.map) return;

      const sourceIds: string[] = [];
      const layerIds: string[] = [];

      if (config.type === 'raster') {
        // Add raster source and layer
        const sourceId = `${layerId}-source`;
        const layerId_ = `${layerId}-layer`;

        this.mapInstance.map.addSource(sourceId, {
          type: 'raster',
          url: config.url,
          tileSize: 256,
        });
        sourceIds.push(sourceId);

        this.mapInstance.map.addLayer({
          id: layerId_,
          type: 'raster',
          source: sourceId,
          layout: { visibility: 'none' },
        });
        layerIds.push(layerId_);
      } else if (config.type === 'style') {
        // Check cache first
        let styleJson = styleCache.get(config.style!);

        if (!styleJson) {
          // Fetch the style and add its layers
          const response = await fetch(
            `https://api.mapbox.com/styles/v1${config.style!.replace(
              'mapbox://styles',
              ''
            )}?access_token=${mapboxgl.accessToken}`
          );
          styleJson = await response.json();
          // Cache the fetched style
          styleCache.set(config.style!, styleJson);
        }

        // Add sources from the style
        Object.entries(styleJson.sources).forEach(([id, source]) => {
          const sourceId = `${layerId}-${id}`;
          if (!this.mapInstance.map?.getSource(sourceId)) {
            this.mapInstance.map?.addSource(sourceId, source as any);
            sourceIds.push(sourceId);
          }
        });

        // Add layers from the style
        styleJson.layers.forEach((layer: any) => {
          const layerId_ = `${layerId}-${layer.id}`;
          const newLayer = {
            ...layer,
            id: layerId_,
            source: layer.source ? `${layerId}-${layer.source}` : undefined,
            'source-layer': layer['source-layer'],
            layout: {
              ...layer.layout,
              visibility: 'none',
            },
          };

          if (!this.mapInstance.map?.getLayer(layerId_)) {
            this.mapInstance.map?.addLayer(newLayer);
            layerIds.push(layerId_);
          }
        });
      }

      // Add terrain and sky if configured
      if (config.terrain && !this.mapInstance.map.getSource('mapbox-dem')) {
        this.mapInstance.map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 256,
        });
        this.mapInstance.map.setTerrain({
          source: 'mapbox-dem',
          exaggeration: 1,
        });
      }

      if (config.sky && !this.mapInstance.map.getLayer('sky')) {
        this.mapInstance.map.addLayer({
          id: 'sky',
          type: 'sky',
          paint: {
            'sky-type': 'atmosphere',
            'sky-atmosphere-sun': [0.0, 0.0],
            'sky-atmosphere-sun-intensity': 15,
          },
        });
      }

      // Store the source and layer IDs for this base layer
      this.mapInstance.baseLayerSourceIds.set(layerId, sourceIds);
      this.mapInstance.baseLayerLayerIds.set(layerId, layerIds);

      config.initialized = true;

      // Move base layers to bottom to ensure custom layers are visible
      this.moveBaseLayersToBottom(layerId);
    },

    /**
     * Move base layers to the bottom of the layer stack
     */
    moveBaseLayersToBottom(baseLayerId: string) {
      if (!this.mapInstance.map) return;

      const baseLayerIds = this.mapInstance.baseLayerLayerIds.get(baseLayerId) || [];
      const allLayers = this.mapInstance.map.getStyle().layers || [];

      // Find first non-base layer (custom layer)
      const firstCustomLayer = allLayers.find((layer: any) => {
        // Check if this layer is NOT a base layer
        let isBaseLayer = false;

        // Check all base layer registries
        this.mapInstance.baseLayerLayerIds.forEach((ids) => {
          if (ids.includes(layer.id)) {
            isBaseLayer = true;
          }
        });

        // Also skip sky and terrain related layers
        if (layer.id === 'sky' || layer.source === 'mapbox-dem') {
          isBaseLayer = true;
        }

        return !isBaseLayer;
      });

      if (firstCustomLayer) {
        // Move each base layer below the first custom layer
        for (const layerId of baseLayerIds) {
          if (this.mapInstance.map?.getLayer(layerId)) {
            try {
              this.mapInstance.map.moveLayer(layerId, firstCustomLayer.id);
            } catch (e) {
              // Layer might not exist or already be in correct position
            }
          }
        }
      }
    },

    /**
     * Switch to a different base layer
     */
    async switchBaseLayer(newLayerId: string) {
      if (!this.mapInstance.map) return;

      // Hide all base layers
      for (const [id, config] of this.mapInstance.baseLayerRegistry) {
        if (config.visible) {
          await this.setBaseLayerVisibility(id, false);
        }
      }

      // Initialize new layer if needed (lazy load)
      const newConfig = this.mapInstance.baseLayerRegistry.get(newLayerId);
      if (newConfig && !newConfig.initialized) {
        await this.initializeBaseLayer(newLayerId);
      }

      // Show new base layer
      await this.setBaseLayerVisibility(newLayerId, true);
      this.selectedBaseLayer = newLayerId as BaseLayersType;
    },

    /**
     * Set visibility of a base layer
     */
    setBaseLayerVisibility(layerId: string, visible: boolean) {
      const config = this.mapInstance.baseLayerRegistry.get(layerId);
      if (!config || !config.initialized || !this.mapInstance.map) return;

      const visibility = visible ? 'visible' : 'none';
      const layerIds = this.mapInstance.baseLayerLayerIds.get(layerId) || [];

      // Batch all visibility changes for better performance
      requestAnimationFrame(() => {
        // Set visibility for all layers of this base layer in a single batch
        for (const id of layerIds) {
          if (this.mapInstance.map?.getLayer(id)) {
            this.mapInstance.map.setLayoutProperty(id, 'visibility', visibility);
          }
        }

        // When showing a base layer, ensure it's at the bottom
        if (visible) {
          this.moveBaseLayersToBottom(layerId);
        }
      });

      config.visible = visible;
    },


    /**
     * Adds an event listener to the map, check if it exists before adding it and stores it in the addedListeners array for later automatic cleanup.
     *
     * @param event - The type of event to listen for.
     * @param func - The callback function to be executed when the event occurs.
     * @param layer - Optional. The layer to which the event listener should be added.
     */
    addEventToMap(
      event: MapEventType,
      func: (...args: any[]) => void,
      layer?: string | string[]
    ): void {
      const mapInstance = this.mapInstance.map;

      // Create a unique key for this event listener
      const listenerKey = `${event}-${layer ? (Array.isArray(layer) ? layer.join(',') : layer) : 'global'}-${func.toString().substring(0, 50)}`;

      // Check if this listener already exists (O(1) lookup)
      if (eventListenerMap.has(listenerKey)) return;

      if (mapInstance) {
        if (layer) {
          mapInstance.on(event, layer, func);
        } else {
          mapInstance.on(event, func);
        }
      }

      const listener: {
        event: MapEventType;
        function: (...args: any[]) => void;
        layer?: string | string[];
      } = {
        event,
        function: func,
      };

      if (layer) {
        listener.layer = layer;
      }

      this.mapInstance.addedListeners.push(listener);
      eventListenerMap.set(listenerKey, func);
    },

    /**
     * Cleans up the map by removing custom (non-base) layers and sources
     * @returns {Promise<void>} A promise that resolves when the map has been cleaned up.
     */
    async cleanMap() {
      if (this.mapInstance.map) {
        const map = this.mapInstance.map;

        // Collect all base layer IDs
        const allBaseLayerIds = new Set<string>();
        const allBaseSourceIds = new Set<string>();

        for (const ids of this.mapInstance.baseLayerLayerIds.values()) {
          for (const id of ids) {
            allBaseLayerIds.add(id);
          }
        }

        for (const ids of this.mapInstance.baseLayerSourceIds.values()) {
          for (const id of ids) {
            allBaseSourceIds.add(id);
          }
        }

        // Also add terrain and sky as base
        allBaseLayerIds.add('sky');
        allBaseSourceIds.add('mapbox-dem');

        // Remove non-base layers
        const removeLayers =
          map.getStyle()?.layers.map(async (layer: any) => {
            if (!allBaseLayerIds.has(layer.id)) {
              if (map.getLayer(layer.id)) {
                map.removeLayer(layer.id);
              }
            }
          }) ?? [];

        // Remove non-base sources
        const removeSources = Object.keys(map.getStyle()?.sources || {}).map(
          async (sourceId) => {
            if (!allBaseSourceIds.has(sourceId)) {
              if (map.getSource(sourceId)) {
                map.removeSource(sourceId);
              }
            }
          }
        );

        // Remove all event listeners
        const removeListeners = this.mapInstance.addedListeners.map(
          async (listener: any) => {
            if (listener.layer) {
              map.off(
                listener.event,
                listener.layer,
                listener.function
              );
            } else {
              map.off(listener.event, listener.function);
            }
          }
        );

        this.mapInstance.addedListeners = [];
        this.mapInstance.loadedTiles = [];
        eventListenerMap.clear();

        await Promise.all([
          ...removeLayers,
          ...removeSources,
          ...removeListeners,
        ]);

        this.hoverFarms = [];
        this.hoverFarmTooltip = {
          show: false,
          farms: [],
          x: 0,
          y: 0,
        };
      }
    },

    setMapMaxZoom(maxZoom: number) {
      this.mapMaxZoom = maxZoom;
      if (this.mapInstance.map) {
        this.mapInstance.map.setMaxZoom(maxZoom);
      }
    },

    setMapMinZoom(minZoom: number) {
      this.mapMinZoom = minZoom;
      if (this.mapInstance.map) {
        this.mapInstance.map.setMinZoom(minZoom);
      }
    },


    /**
     * Sets the current zoom level based on the commodity type
     * @param {string} [commodity] - Optional commodity type to determine zoom threshold
     * @description Uses the commodity-specific zoom threshold if found, otherwise falls back to the default mapview zoom threshold
     */
    setCurrentZoomLevel(commodity?: string) {
      this.currentZoomLevel =
        this.zoomLevels.commodity_zoom_threshold.find(
          (zoom: { commodity: string; zoom_threshold: number }) => zoom.commodity === commodity
        )?.zoom_threshold ?? this.zoomLevels.mapview_zoom_threshold;
    },

    /**
     * Fetches map zoom level configuration from the API
     * @description Retrieves both the default mapview zoom threshold and commodity-specific zoom thresholds
     * @throws Handles API errors with the error handling service
     */
    async getZoomLevels() {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const response = await apiStore.service.post<ZoomLevels>(
          '/dataset/record/farmplot/config'
        );
        this.zoomLevels = response.data;
      } catch (e) {
        handleError('Error getting map zoom levels', e);
      }
    },
  },
});
